<script setup lang="ts">
import { StarterKit } from '@tiptap/starter-kit'
import { ImageResize } from 'tiptap-extension-resize-image'
import { FontSize } from 'tiptap-extension-font-size'
import { TextStyle } from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import { Link } from '@tiptap/extension-link'
import { CharacterCount } from '@tiptap/extension-character-count'
import { TextAlign } from '@tiptap/extension-text-align'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { Youtube } from '@tiptap/extension-youtube'
import { FileHandler } from '@tiptap-pro/extension-file-handler'
import { VideoPlayer } from '@videojs-player/vue'
import TipTapVideoExtension from '@/extensions/TipTapVideoExtension'
import { safeGenerateHTML } from '@/utils/safeGenerateHTML'
import type { TableOptions } from '@core/stores/user'

const { t } = useI18n()
const table = 'library'

definePage({
  meta: {
    action: 'read',
    subject: 'library',
  },
})

const defaultTableOptions: TableOptions = {
  sortBy: 'id',
  orderBy: 'asc',
  page: 1,
  perPage: 100,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const currenItem = ref()
const isAddDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)

const { data: itemsData } = await useApi<any>(createUrl(`/${table}/tiles`,
  {
    query: {
      search: debouncedSearch,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
    },
  },
))

const urls = ref<Record<string, { url: string; preview: string }>>({})

const getMedia = async (id: string) => {
  const { data } = await useApi<any>(`/library/${id}`)

  if (data.value) {
    urls.value[id] = {
      url: data.value.cloudUrl,
      preview: data.value.previewUrl,
    }
  }
}

for (const item of itemsData.value?.data ?? [])
  getMedia(item.id)

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([page, itemsPerPage, sortBy, orderBy], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
  })
})

const editorExtensions = [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3],
    },
  }),
  ImageResize.configure({
    allowBase64: true,
    inline: true,
  }),
  FontSize,
  TextStyle,
  Underline,
  TipTapVideoExtension.configure({ HTMLAttributes: { class: 'tiptap-video', controls: true } }),
  Link.configure(
    {
      openOnClick: false,
    },
  ),
  CharacterCount.configure({
    limit: 240,
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,
  Youtube.configure({
    inline: false,
    nocookie: true,
  }),
  FileHandler.configure({
    allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    onDrop: (currentEditor, files, pos) => {
      files.forEach(file => {
        const fileReader = new FileReader()

        fileReader.readAsDataURL(file)
        fileReader.onload = () => {
          currentEditor.chain().insertContentAt(pos, {
            type: 'image',
            attrs: {
              src: fileReader.result,
            },
          }).focus().run()
        }
      })
    },
    onPaste: (currentEditor, files) => {
      files.forEach(file => {
        const fileReader = new FileReader()

        fileReader.readAsDataURL(file)
        fileReader.onload = () => {
          currentEditor.chain().insertContentAt(currentEditor.state.selection.anchor, {
            type: 'image',
            attrs: {
              src: fileReader.result,
            },
          }).focus().run()
        }
      })
    },
  }),
]
</script>

<template>
  <div>
    <VCard>
      <VCardTitle>
        <VBtn
          :to="{ name: 'games-library-video-tiles' }"
          disabled
          variant="outlined"
          class="my-2 mr-4"
        >
          <VIcon
            start
            icon="tabler-video"
          />
          {{ t('library-page.video') }}
        </VBtn>
        <VBtn
          :to="{ name: 'games-library-scorm-tiles' }"
          variant="outlined"
          class="my-2"
        >
          <VIcon
            start
            icon="tabler-books"
          />
          {{ t('library-page.courses') }}
        </VBtn>
      </VCardTitle>
      <VCardText class="mt-2">
        <div v-if="!itemsData?.data?.length">
          {{ t('$vuetify.noDataText') }}
        </div>

        <VRow v-else>
          <VCol
            v-for="item in itemsData?.data ?? []"
            :key="item.id"
            cols="12"
            md="4"
            sm="6"
          >
            <VCard
              v-if="urls[item.id]?.url"
              flat
              border
            >
              <div class="px-2 pt-2">
                <VideoPlayer
                  :src="urls[item.id]?.url"
                  :poster="urls[item.id]?.preview"
                  controls
                  plays-inline
                  :height="$vuetify.display.mdAndUp ? 200 : 150"
                  class="w-100 rounded"
                />
              </div>
              <VCardText>
                <h5 class="text-h5 mb-2">
                  {{ item.title }}
                </h5>
                <div
                  v-if="item.text"
                  v-dompurify-html="safeGenerateHTML(
                    item.text, editorExtensions)"
                  class="description-content text-body-1 mb-0"
                />
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@import "video.js/dist/video-js.css";

.ProseMirror-focused{
  border: none;
}
</style>
